/**
 * Opening a plan, and establishing the coordinate space everything else is expressed in.
 *
 * This is the file most able to be quietly wrong. `previewWidthPixels` and
 * `previewHeightPixels` are the only coupling between this browser and the recognition engine:
 * the engine believes whatever they say and maps every result into that space. Declare them
 * wrong and recognition still succeeds, still returns geometry, and the geometry is in the
 * wrong place - there is no error to see.
 *
 * So the rule is not restated here. `packages/conversion-core` owns it, is unit-tested against
 * real schema-39 packages, and is imported rather than reimplemented.
 *
 * The display resolution is a separate concern. The canvas may rasterize at whatever scale
 * looks sharp on the operator's screen; the polygon and calibration points are always reported
 * in preview space, never in the pixels they were clicked at.
 */

import {
  previewSizeForPdfPage,
  previewSizeForRasterSource,
  type Size,
} from "@plan-forge/conversion-core";
import type { PDFDocumentProxy } from "pdfjs-dist";

/**
 * pdf.js, loaded on first use rather than at startup.
 *
 * It is most of this application's JavaScript, and an operator converting a PNG never needs
 * any of it. Importing it statically put it in the initial bundle and made every load pay for
 * a format that may not be opened.
 *
 * Cached, because the second PDF should not re-import it.
 */
let pdfjsModule: typeof import("pdfjs-dist") | null = null;

async function loadPdfjs(): Promise<typeof import("pdfjs-dist")> {
  if (pdfjsModule !== null) return pdfjsModule;
  const [module, { default: workerUrl }] = await Promise.all([
    import("pdfjs-dist"),
    // pdf.js parses in a worker so a large sheet does not freeze the interface. Vite resolves
    // this to a real URL at build time; without it, pdf.js tries to fetch a bare specifier.
    import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
  ]);
  module.GlobalWorkerOptions.workerSrc = workerUrl;
  pdfjsModule = module;
  return module;
}

export type SourceKind = "pdf" | "raster";

export interface PlanPage {
  /** 1-based, and 1 for a raster source. */
  readonly pageNumber: number;
  /**
   * The coordinate space the polygon, the calibration points and every engine result live in.
   * For a PDF, the effective page size in points at scale 1.0 - effective meaning after
   * /Rotate. For an image, its native pixels.
   */
  readonly preview: Size;
  /** Draws this page onto a canvas at `scale` times preview size. */
  render(canvas: HTMLCanvasElement, scale: number): Promise<void>;
}

export interface PlanSource {
  readonly kind: SourceKind;
  readonly fileName: string;
  readonly contentType: "application/pdf" | "image/png" | "image/jpeg";
  readonly pageCount: number;
  page(pageNumber: number): Promise<PlanPage>;
  dispose(): void;
}

const ACCEPTED: Readonly<Record<string, "application/pdf" | "image/png" | "image/jpeg">> = {
  "application/pdf": "application/pdf",
  "image/png": "image/png",
  "image/jpeg": "image/jpeg",
};

export class UnsupportedSourceError extends Error {
  constructor(type: string) {
    super(
      `${type || "That file"} cannot be converted. The engine reads PDF, PNG and JPEG.`,
    );
    this.name = "UnsupportedSourceError";
  }
}

export async function openPlan(file: File): Promise<PlanSource> {
  const contentType = ACCEPTED[file.type];
  if (contentType === undefined) throw new UnsupportedSourceError(file.type);
  return contentType === "application/pdf" ? openPdf(file) : openRaster(file, contentType);
}

async function openPdf(file: File): Promise<PlanSource> {
  const pdfjs = await loadPdfjs();
  const data = new Uint8Array(await file.arrayBuffer());
  // The loading task, not just the document: destroying the task is what releases the worker
  // as well as the parsed document, and the worker is the expensive half.
  const task = pdfjs.getDocument({ data });
  const document: PDFDocumentProxy = await task.promise;

  return {
    kind: "pdf",
    fileName: file.name,
    contentType: "application/pdf",
    pageCount: document.numPages,

    async page(pageNumber) {
      const page = await document.getPage(pageNumber);

      // page.view is the MediaBox as [x0, y0, x1, y1] in points, before rotation. page.rotate
      // is /Rotate. conversion-core turns the pair into the effective size, which is the whole
      // reason both are read rather than taking a viewport's dimensions and hoping.
      const [x0, y0, x1, y1] = page.view as [number, number, number, number];
      const mediaBox: Size = { width: x1 - x0, height: y1 - y0 };
      const preview = previewSizeForPdfPage(mediaBox, page.rotate);

      return {
        pageNumber,
        preview,
        async render(canvas, scale) {
          // pdf.js applies /Rotate itself at scale 1, so this viewport's dimensions are the
          // effective size - the same number conversion-core computed. They agree by
          // construction rather than by coincidence.
          const viewport = page.getViewport({ scale });
          const context = canvas.getContext("2d");
          if (context === null) throw new Error("This browser did not provide a 2D canvas context.");

          canvas.width = Math.round(viewport.width);
          canvas.height = Math.round(viewport.height);
          // Annotations off: the takeoff markup on an estimating copy is stored as PDF
          // annotations, which pdf.js draws by default and the engine never reads. Showing them
          // makes the preview disagree with what will be converted.
          await page.render({
            canvas,
            canvasContext: context,
            viewport,
            annotationMode: pdfjs.AnnotationMode.DISABLE,
          }).promise;
        },
      };
    },

    dispose() {
      void task.destroy();
    },
  };
}

async function openRaster(
  file: File,
  contentType: "image/png" | "image/jpeg",
): Promise<PlanSource> {
  // createImageBitmap decodes off the main thread, which matters: a 6048x4320 sheet decoded
  // synchronously is a visible freeze.
  const bitmap = await createImageBitmap(file);
  const preview = previewSizeForRasterSource({ width: bitmap.width, height: bitmap.height });

  return {
    kind: "raster",
    fileName: file.name,
    contentType,
    pageCount: 1,

    async page(pageNumber) {
      return {
        pageNumber,
        preview,
        async render(canvas, scale) {
          const context = canvas.getContext("2d");
          if (context === null) throw new Error("This browser did not provide a 2D canvas context.");

          canvas.width = Math.round(preview.width * scale);
          canvas.height = Math.round(preview.height * scale);
          context.imageSmoothingQuality = "high";
          context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        },
      };
    },

    dispose() {
      bitmap.close();
    },
  };
}
